'use client';

import {
  useState,
  useEffect,
  type FC,
  type ChangeEvent,
  type FormEvent,
  useContext,
  Suspense,
} from 'react';
import { useSearchParams } from 'next/navigation';
import {
  getPreferredSignInMethod,
  sendEmailVerificationCode,
  sendSMSVerificationCode,
} from '@/_api/auth';
import { changeLoginMethod } from '@/_api/users';
import {
  Separator,
  Flex,
  Input,
  Button,
  ButtonGroup,
  Alert,
} from '@/_components';
import type { LoginProps } from './Login.types';
import { UserContext } from '@/_contexts/UserProvider';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';

export const Login: FC<LoginProps> = () => {
  const [password, setPassword] = useState<string>('');

  const { addToast } = useContext(ToastContext);
  const { signInPassword, sendMagicLink } = useContext(UserContext);
  const { uid, username, setUsername, setPhoneNumber, setLoginState, setUid } =
    useContext(LoginContext);

  const [isUsernameDisabled, setUsernameDisabled] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);
  const [showSMSFailed, setShowSMSFailed] = useState<boolean>(false);
  const searchParams = useSearchParams();

  useEffect(() => {
    return () => {
      setUsernameDisabled(false);
      setLoading(false);
    };
  }, []);

  const onFormSubmit = async (event: FormEvent) => {
    event?.preventDefault();

    if (!username) {
      addToast('info', 'Please fill out email first.');
      return;
    }

    setUsernameDisabled(true);
    setLoading(true);

    try {
      await onGetPreferredMethod();
    } catch (error) {
      setUsernameDisabled(false);
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const onLoginWithPassword = async (event: FormEvent) => {
    event?.preventDefault?.();

    if (!username) {
      return addToast('error', 'Email is missing');
    }

    if (!password) {
      return addToast('error', 'Password is missing');
    }

    setLoading(true);

    try {
      await signInPassword(username, password);
    } catch (error: any) {
      if (error.message === 'INVALID_PASSWORD') {
        return addToast('error', 'Invalid password');
      }

      if (error.message.includes('TOO_MANY_ATTEMPTS_TRY_LATER')) {
        return addToast(
          'error',
          'Access to this account has been temporarily restricted. Please try again later or reset your password for immediate access.',
        );
      }

      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  // Get preferred method for the user
  const onGetPreferredMethod = async () => {
    try {
      const preferredSignInMethod = await getPreferredSignInMethod({
        username,
      });

      // Catch generic error
      if (!!preferredSignInMethod?.error) {
        setUsernameDisabled(false);
        return addToast('error', null, { error: preferredSignInMethod?.error });
      }

      // We return null only if email doesn't exists
      if (preferredSignInMethod === null) {
        setUsernameDisabled(false);
        return addToast(
          'error',
          "Sorry, we couldn't find a user with that email address",
        );
      }

      const { accountant_id, uid, preferred_sign_in_method } =
        preferredSignInMethod;

      // Sync uid
      setUid(uid);

      const params = new URLSearchParams(globalThis.location.search);
      const redirect = params.get('redirect');

      switch (preferred_sign_in_method) {
        case 'password':
          setShowPasswordField(true);
          break;
        case 'email': {
          const response = (await sendEmailVerificationCode({
            uid,
          })) as unknown as Response; // TODO: `_callBackend` refactoring needed. See SOR-13473.

          const { status } = response;

          if (status === 429) {
            addToast('error', (await response.json())?.message);
            return;
          }

          setLoginState('sent-email');
          break;
        }
        case 'sms': {
          try {
            const response = (await sendSMSVerificationCode({
              uid,
            })) as unknown as Response; // TODO: `_callBackend` refactoring needed. See SOR-13473.

            const { status } = response;

            if (status === 429) {
              addToast('error', (await response.json())?.message);
              return;
            }

            const { error, phone } = await response.json();

            if (!error) {
              setPhoneNumber(phone);
              setLoginState('sent-text');
            } else {
              setShowSMSFailed(true);
            }
          } catch (error) {
            addToast('error', 'Failed to send SMS code', { error });
            setShowSMSFailed(true);
          }

          break;
        }
        default:
          sendMagicLink(accountant_id, redirect, uid);
          setLoginState('sent-link');
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const onUseAlternateSignIn = async () => {
    const redirect: string | null = searchParams.get('redirect');
    const questionnaireId: string | null = searchParams.get('questionnaireId');

    const redirectUrl: string =
      redirect && questionnaireId
        ? `${redirect}?questionnaireId=${questionnaireId}`
        : redirect || '';

    try {
      const response = (await changeLoginMethod({
        uid,
        username,
        redirect: redirectUrl,
      })) as unknown as Response; // TODO: `_callBackend` refactoring needed. See SOR-13473.

      const { status } = response;

      if (status === 429) {
        addToast('error', (await response.json())?.message);
        return;
      }

      if (showSMSFailed) setShowSMSFailed(false);
      return setLoginState('alternate-sign-in');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const onUsernameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setUsername(event.target.value?.trim()?.toLowerCase());

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) =>
    setPassword(event.target.value);

  const onEditUsername = () => {
    setUsernameDisabled(false);
    setShowPasswordField(false);
  };

  return (
    <Suspense>
      <form
        onSubmit={showPasswordField ? onLoginWithPassword : onFormSubmit}
        className="flex flex-column"
      >
        {showSMSFailed ? (
          <>
            <Flex gap={12} padding={10} isColumn={true}>
              <Alert type="danger">
                SMS code failed to send. Your phone number might be invalid.
                Please use the button below to reset your sign-in method.
              </Alert>
              <ButtonGroup isSameLine={true} align="space-between">
                <Button
                  value="Back"
                  intent="secondary"
                  type="button"
                  iconLeft="arrow-left"
                  onClick={() => {
                    setShowSMSFailed(false);
                  }}
                />
                <Button
                  value="Reset My Sign In Method"
                  intent="primary"
                  type="button"
                  onClick={onUseAlternateSignIn}
                />
              </ButtonGroup>
            </Flex>
          </>
        ) : (
          <>
            <Flex padding={10} isColumn={true} gap={6}>
              <Input
                value={username}
                label="Email"
                onChange={onUsernameChange}
                placeholder="john@example.com"
                isDisabled={isUsernameDisabled}
                iconRight={isUsernameDisabled ? 'pencil' : undefined}
                onRightClick={isUsernameDisabled ? onEditUsername : undefined}
                data-testid="Login-email-input"
              />
              {showPasswordField && (
                <Input
                  label="Password"
                  value={password}
                  onChange={onPasswordChange}
                  type="password"
                  iconRight="eye"
                  placeholder="Password"
                  data-testid="Login-password-input"
                />
              )}
            </Flex>
            <Separator isLight={true} />
            <Flex padding={10} isColumn={true} gap={6}>
              <ButtonGroup isFullWidth={true}>
                <Button
                  value="Sign in"
                  intent="primary"
                  data-testid={
                    showPasswordField
                      ? 'Login-password-submit-button'
                      : 'Login-email-submit-button'
                  }
                  type="submit"
                  iconLeft="key"
                  onClick={
                    showPasswordField ? onLoginWithPassword : onFormSubmit
                  }
                  isDisabled={isLoading}
                  isLoading={isLoading}
                />
              </ButtonGroup>
              {showPasswordField && (
                <>
                  <Separator isLight={true} />
                  <ButtonGroup
                    align="space-between"
                    className="flex-row"
                    isSameLine={true}
                  >
                    <Button
                      value="Forgot Password?"
                      intent="secondary"
                      type="button"
                      onClick={() => setLoginState('forgot-password')}
                      isDisabled={isLoading}
                    />
                    <Button
                      value="Use Alternate Sign-in"
                      intent="secondary"
                      type="button"
                      onClick={onUseAlternateSignIn}
                      isDisabled={isLoading}
                    />
                  </ButtonGroup>
                </>
              )}
            </Flex>
          </>
        )}
      </form>
    </Suspense>
  );
};
